/**
 * What the Overview's Features and Permissions tiles list (ISS-024 — both used to open the
 * security-groups list). Built only from the org-doc cache: the embedded orgFeatures,
 * orgSecurityGroups, orgHats and orgUsers maps (CLAUDE.md "FE-reads-only-the-cache").
 *
 * A security group grants a feature through its `featureDetails` map (feature → permissions);
 * the `features` array is read too, for groups written before featureDetails existed.
 */

export interface CatalogPermissionRef {
    key: string;
    label: string;
    description: string;
}

/** A security group, hat or person named with its key, so the details window can open it. */
export interface CatalogRef {
    key: string;
    name: string;
}

export interface CatalogRefs {
    securityGroups: CatalogRef[];
    hats: CatalogRef[];
    staff: CatalogRef[];
}

export interface FeatureCatalogEntry {
    key: string;
    name: string;
    description: string;
    permissions: CatalogPermissionRef[];
    /** Security groups that grant this feature. */
    securityGroups: string[];
    /** Hats that carry one of those groups. */
    hats: string[];
    /** Staff who wear one of those hats. */
    staff: string[];
    refs: CatalogRefs;
}

export interface PermissionCatalogEntry extends CatalogPermissionRef {
    featureKey: string;
    featureName: string;
    /** True when at least one security group grants it. */
    granted: boolean;
    securityGroups: string[];
    hats: string[];
    staff: string[];
    refs: CatalogRefs;
}

type AnyRecord = Record<string, any>;

interface GroupReach {
    key: string;
    name: string;
    hats: CatalogRef[];
    staff: CatalogRef[];
    /** feature key → permission keys this group grants */
    grants: Map<string, string[]>;
}

const sorted = (values: Iterable<string>): string[] =>
    Array.from(new Set(values))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

/** `view-company-client-abc` → `View company client abc`. */
export function humanizePermission(key: string): string {
    const words = (key || '').split(/[-_]+/).filter(Boolean).join(' ');
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function embedded(cache: AnyRecord | undefined, keysField: string): [string, AnyRecord][] {
    const keys: string[] = (cache?.[keysField] as string[]) || [];
    return keys
        .map((k) => [k, cache?.[k]] as [string, AnyRecord])
        .filter(([, v]) => v != null && typeof v === 'object');
}

function groupReach(org: AnyRecord): Map<string, GroupReach> {
    const userName = (uid: string): string => {
        const u = org?.['orgUsers']?.[uid];
        return (u && (u.name || u.userName)) || '';
    };
    const reach = new Map<string, GroupReach>();
    for (const [key, sg] of embedded(org?.['orgSecurityGroups'], 'securityGroupsKeys')) {
        const grants = new Map<string, string[]>();
        for (const f of (sg['features'] as string[]) || []) grants.set(f, []);
        for (const [f, detail] of Object.entries((sg['featureDetails'] as AnyRecord) || {})) {
            grants.set(f, Array.isArray(detail?.permissions) ? detail.permissions : []);
        }
        reach.set(key, { key, name: sg['name'] || key, hats: [], staff: [], grants });
    }
    for (const [hatKey, hat] of embedded(org?.['orgHats'], 'hatsKeys')) {
        const staff = ((hat['assignedToArray'] as string[]) || [])
            .map((uid) => ({ key: uid, name: userName(uid) }))
            .filter((s) => s.name);
        for (const ref of (hat['securityGroupsArray'] as Array<string | { key?: string }>) || []) {
            const group = reach.get(typeof ref === 'string' ? ref : ref?.key || '');
            if (!group) continue;
            if (hat['name']) group.hats.push({ key: hatKey, name: hat['name'] });
            group.staff.push(...staff);
        }
    }
    return reach;
}

/** One entry per key, sorted by name. */
const sortedRefs = (refs: CatalogRef[]): CatalogRef[] =>
    Array.from(
        new Map(refs.filter((r) => r.key && r.name).map((r) => [r.key, r] as [string, CatalogRef])).values()
    ).sort((a, b) => a.name.localeCompare(b.name));

function collect(groups: GroupReach[]): Pick<FeatureCatalogEntry, 'securityGroups' | 'hats' | 'staff' | 'refs'> {
    const hats = groups.flatMap((g) => g.hats);
    const staff = groups.flatMap((g) => g.staff);
    return {
        securityGroups: sorted(groups.map((g) => g.name)),
        hats: sorted(hats.map((h) => h.name)),
        staff: sorted(staff.map((s) => s.name)),
        refs: {
            securityGroups: sortedRefs(groups.map((g) => ({ key: g.key, name: g.name }))),
            hats: sortedRefs(hats),
            staff: sortedRefs(staff),
        },
    };
}

function permissionRef(feature: AnyRecord, key: string): CatalogPermissionRef {
    return {
        key,
        label: humanizePermission(key),
        description: (feature['permissionDescriptions'] as AnyRecord)?.[key] || '',
    };
}

export function buildFeatureCatalog(org: AnyRecord): FeatureCatalogEntry[] {
    const groups = Array.from(groupReach(org).values());
    return embedded(org?.['orgFeatures'], 'featuresKeys')
        .map(([key, feature]) => ({
            key,
            name: feature['name'] || key,
            description: feature['description'] || '',
            permissions: ((feature['permissions'] as string[]) || []).map((p) => permissionRef(feature, p)),
            ...collect(groups.filter((g) => g.grants.has(key))),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildPermissionCatalog(org: AnyRecord): PermissionCatalogEntry[] {
    const groups = Array.from(groupReach(org).values());
    const entries: PermissionCatalogEntry[] = [];
    for (const [featureKey, feature] of embedded(org?.['orgFeatures'], 'featuresKeys')) {
        const offered = (feature['permissions'] as string[]) || [];
        const grantedHere = groups.flatMap((g) => g.grants.get(featureKey) || []);
        for (const key of sorted([...offered, ...grantedHere])) {
            const holders = groups.filter((g) => (g.grants.get(featureKey) || []).includes(key));
            entries.push({
                ...permissionRef(feature, key),
                featureKey,
                featureName: feature['name'] || featureKey,
                granted: holders.length > 0,
                ...collect(holders),
            });
        }
    }
    return entries.sort((a, b) => a.featureName.localeCompare(b.featureName) || a.key.localeCompare(b.key));
}
